import { debugLogger } from '@/utils/debugLogger';

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  category: 'websocket' | 'cache' | 'chat' | 'ui';
}

interface ErrorLog {
  message: string;
  stack?: string;
  timestamp: number;
  category: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export class MonitoringSystem {
  private static instance: MonitoringSystem;
  private metrics: PerformanceMetric[] = [];
  private errors: ErrorLog[] = [];
  private maxMetrics = 1000;
  private maxErrors = 500;

  private constructor() {
    this.setupGlobalErrorHandling();
    this.setupPerformanceMonitoring();
  }

  static getInstance(): MonitoringSystem {
    if (!MonitoringSystem.instance) {
      MonitoringSystem.instance = new MonitoringSystem();
    }
    return MonitoringSystem.instance;
  }

  recordMetric(name: string, value: number, category: PerformanceMetric['category']): void {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      category
    };

    this.metrics.push(metric);

    // Keep only recent metrics
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }

    // Log significant performance issues
    if (this.isPerformanceIssue(metric)) {
      debugLogger.warn(`[Monitoring] Performance issue detected:`, metric);
    }
  }

  recordError(error: Error | string, category: string, severity: ErrorLog['severity'] = 'medium'): void {
    const errorLog: ErrorLog = {
      message: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'object' ? error.stack : undefined,
      timestamp: Date.now(),
      category,
      severity
    };

    this.errors.push(errorLog);

    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Log to console based on severity
    const logMethod = severity === 'critical' ? 'error' : 
                     severity === 'high' ? 'error' :
                     severity === 'medium' ? 'warn' : 'log';
    
    console[logMethod](`[Monitoring] ${severity.toUpperCase()} ${category}:`, errorLog);
  }

  getMetrics(category?: PerformanceMetric['category'], timeRange?: number): PerformanceMetric[] {
    let filtered = this.metrics;

    if (category) {
      filtered = filtered.filter(m => m.category === category);
    }

    if (timeRange) {
      const cutoff = Date.now() - timeRange;
      filtered = filtered.filter(m => m.timestamp > cutoff);
    }

    return filtered;
  }

  getErrors(category?: string, severity?: ErrorLog['severity'], timeRange?: number): ErrorLog[] {
    let filtered = this.errors;

    if (category) {
      filtered = filtered.filter(e => e.category === category);
    }

    if (severity) {
      filtered = filtered.filter(e => e.severity === severity);
    }

    if (timeRange) {
      const cutoff = Date.now() - timeRange;
      filtered = filtered.filter(e => e.timestamp > cutoff);
    }

    return filtered;
  }

  getHealthReport(): {
    websocketConnections: number;
    cacheHitRate: number;
    averageMessageLatency: number;
    errorRate: number;
    recentErrors: ErrorLog[];
    performanceIssues: PerformanceMetric[];
  } {
    const recentMetrics = this.getMetrics(undefined, 300000); // Last 5 minutes
    const recentErrors = this.getErrors(undefined, undefined, 300000);

    const websocketMetrics = recentMetrics.filter(m => m.category === 'websocket');
    const cacheMetrics = recentMetrics.filter(m => m.category === 'cache');
    const chatMetrics = recentMetrics.filter(m => m.category === 'chat' && m.name === 'message_latency');

    return {
      websocketConnections: websocketMetrics.filter(m => m.name === 'connection_count').pop()?.value || 0,
      cacheHitRate: this.calculateCacheHitRate(cacheMetrics),
      averageMessageLatency: this.calculateAverageLatency(chatMetrics),
      errorRate: recentErrors.length,
      recentErrors: recentErrors.slice(-10),
      performanceIssues: recentMetrics.filter(m => this.isPerformanceIssue(m))
    };
  }

  private setupGlobalErrorHandling(): void {
    window.addEventListener('error', (event) => {
      this.recordError(event.error || event.message, 'global', 'high');
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.recordError(event.reason || 'Unhandled promise rejection', 'promise', 'high');
    });
  }

  private setupPerformanceMonitoring(): void {
    // Skip timer creation in test environments
    const IS_TEST =
      (typeof process !== 'undefined' && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test')) ||
      (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE === 'test') ||
      (typeof window !== 'undefined' && (window as any).__VITEST_BROWSER__ === true);

    if (IS_TEST) {
      return; // No timers in tests
    }

    // Monitor WebSocket connection count
    setInterval(() => {
      const connectionCount = document.querySelectorAll('[data-websocket-connection]').length;
      this.recordMetric('connection_count', connectionCount, 'websocket');
    }, 30000);

    // Monitor memory usage if available
    if ('memory' in performance) {
      setInterval(() => {
        const memory = (performance as any).memory;
        this.recordMetric('heap_used', memory.usedJSHeapSize, 'ui');
        this.recordMetric('heap_total', memory.totalJSHeapSize, 'ui');
      }, 60000);
    }
  }

  private isPerformanceIssue(metric: PerformanceMetric): boolean {
    switch (metric.name) {
      case 'message_latency':
        return metric.value > 5000; // 5 seconds
      case 'cache_miss_rate':
        return metric.value > 0.5; // 50%
      case 'websocket_reconnects':
        return metric.value > 3; // More than 3 reconnects
      case 'heap_used':
        return metric.value > 100 * 1024 * 1024; // 100MB
      default:
        return false;
    }
  }

  private calculateCacheHitRate(metrics: PerformanceMetric[]): number {
    const hits = metrics.filter(m => m.name === 'cache_hit').length;
    const misses = metrics.filter(m => m.name === 'cache_miss').length;
    const total = hits + misses;
    return total > 0 ? hits / total : 0;
  }

  private calculateAverageLatency(metrics: PerformanceMetric[]): number {
    if (metrics.length === 0) return 0;
    const sum = metrics.reduce((acc, m) => acc + m.value, 0);
    return sum / metrics.length;
  }
}

// In tests, avoid instantiating the real MonitoringSystem to prevent timers/listeners
const __isVitest = typeof import.meta !== 'undefined' && (import.meta as any).env?.VITEST;
export const monitoringSystem = __isVitest
  ? (new (class {
      recordMetric = (_name: string, _value: number, _category: any) => {};
      recordError = (_error: any, _category: string, _severity?: any) => {};
      getMetrics = (_category?: any, _timeRange?: number) => [] as any[];
      getErrors = (_category?: string, _severity?: any, _timeRange?: number) => [] as any[];
      getHealthReport = () => ({
        websocketConnections: 0,
        cacheHitRate: 0,
        averageMessageLatency: 0,
        errorRate: 0,
        recentErrors: [],
        performanceIssues: [],
      });
    })()) as unknown as MonitoringSystem
  : MonitoringSystem.getInstance();

// Debug utilities for development
if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
  (window as any).monitoring = {
    getMetrics: (category?: string, timeRange?: number) => 
      monitoringSystem.getMetrics(category as any, timeRange),
    getErrors: (category?: string, severity?: string, timeRange?: number) => 
      monitoringSystem.getErrors(category, severity as any, timeRange),
    getHealthReport: () => monitoringSystem.getHealthReport(),
    recordMetric: (name: string, value: number, category: string) =>
      monitoringSystem.recordMetric(name, value, category as any),
    recordError: (error: string, category: string, severity?: string) =>
      monitoringSystem.recordError(error, category, severity as any)
  };
}
